/**
 * Room yaratish — docs/SPEC.md §create.
 *
 * Bo'limlar: NOMI · MAVZU (6 chip) · TURI (2 ta) · MIKROFON O'RINLARI.
 *
 * **Ikkita narsa ATAYLAB yo'q** va qo'shilmaydi:
 *   · pullik kirish (D-007, SPEC X-3)
 *   · TOPga chiqarish kartasi (X-4, D-006 — TOP faqat `score` bo'yicha)
 *
 * Sig'im variantlari prototipdan (5/8/12/15), yuqori chegara esa
 * SERVERDAN (`limits.room_capacity`) — qoida 22.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { uz } from '../i18n/uz';
import { useConfig } from '../lib/config/useConfig';
import { useCreateRoom } from '../lib/api/useRoom';
import { CTAButton, Chip, Icon, Toast } from '../ui';
import { useToast } from '../ui/useToast';
import './screens.css';

const TOPICS = ['Do‘stlashuv', 'Musiqa', 'Til', 'O‘yin', 'Podkast', 'Erkin'] as const;
const SEAT_CHOICES = [5, 8, 12, 15] as const;

export function Create() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: config } = useConfig();
  const create = useCreateRoom();

  const [title, setTitle] = useState('');
  const [topic, setTopic] = useState<string>(TOPICS[0]);
  const [type, setType] = useState<'open' | 'password'>('open');
  const [password, setPassword] = useState('');
  const [capacity, setCapacity] = useState<number>(SEAT_CHOICES[2]);

  // Chegara SERVERDAN. Klientda qattiq yozilgan 60 bo'lsa, adminka uni
  // pasaytirganda ekran eskirgan variantni ko'rsatib turardi.
  const maxCapacity = Number(config?.limits['room_capacity'] ?? 0);
  const choices = maxCapacity > 0 ? SEAT_CHOICES.filter((n) => n <= maxCapacity) : SEAT_CHOICES;

  const titleOk = title.trim().length >= 3 && title.trim().length <= 40;
  const passwordOk = type === 'open' || password.trim().length >= 4;

  async function submit(): Promise<void> {
    try {
      const room = await create.mutateAsync({
        title: title.trim(),
        topic,
        type,
        capacity,
        ...(type === 'password' ? { password: password.trim() } : {}),
      });
      navigate(`/room/${room.id}`, { replace: true });
    } catch (error) {
      toast.show((error as { message?: string }).message ?? uz.common.error);
    }
  }

  return (
    <div className="screen">
      {toast.message && <Toast message={toast.message} />}

      <div className="screen-bar">
        <button type="button" className="icon-btn" onClick={() => navigate(-1)}>
          <Icon name="back" size={18} />
        </button>
        <h1 className="screen-bar__title">{uz.create.title}</h1>
        <span style={{ width: 40 }} />
      </div>

      <div className="field-label">{uz.create.name}</div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={uz.create.namePlaceholder}
        maxLength={40}
      />

      <div className="field-label">{uz.create.topic}</div>
      <div className="chip-row">
        {TOPICS.map((value) => (
          <Chip key={value} active={topic === value} onClick={() => setTopic(value)}>
            {value}
          </Chip>
        ))}
      </div>

      <div className="field-label">{uz.create.type}</div>
      <div className="type-grid">
        <button
          type="button"
          className={type === 'open' ? 'type-card type-card--on' : 'type-card'}
          onClick={() => setType('open')}
        >
          <Icon name="globe" size={18} />
          <span className="type-card__name">{uz.create.typeOpen}</span>
          <span className="type-card__hint">{uz.create.typeOpenHint}</span>
        </button>
        <button
          type="button"
          className={type === 'password' ? 'type-card type-card--on' : 'type-card'}
          onClick={() => setType('password')}
        >
          <Icon name="lock" size={18} />
          <span className="type-card__name">{uz.create.typeLocked}</span>
          <span className="type-card__hint">{uz.create.typeLockedHint}</span>
        </button>
      </div>

      {type === 'password' && (
        <input
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={uz.create.passwordPlaceholder}
          maxLength={64}
          style={{ marginTop: 10 }}
        />
      )}

      <div className="field-label">{uz.create.seats}</div>
      <div className="chip-row">
        {choices.map((value) => (
          <Chip
            key={value}
            className="chip--age tnum"
            active={capacity === value}
            onClick={() => setCapacity(value)}
          >
            {String(value)}
          </Chip>
        ))}
      </div>

      <div style={{ marginTop: 24 }}>
        <CTAButton
          variant="match"
          disabled={!titleOk || !passwordOk || create.isPending}
          onClick={() => void submit()}
        >
          {uz.create.start}
        </CTAButton>
      </div>

      <div className="screen-tail screen-tail--flat" />
    </div>
  );
}
